import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { reportDevicePresence } from '../services/presenceService'
import {
  getRemoteHeartbeatIntervalMs,
  isBackgroundTrafficPaused,
  withHeartbeatJitter,
} from '../services/apiConfig'

const PRESENCE_INTERVAL_MS = 60_000

/**
 * Reports device presence (online, current page, data activity) to the server.
 */
export function usePresenceReporter(enabled: boolean = true) {
  const location = useLocation()

  useEffect(() => {
    if (!enabled) return

    const send = (force = false) => {
      if (!force && isBackgroundTrafficPaused()) return
      void reportDevicePresence(location.pathname, navigator.onLine)
    }

    send(true)

    const intervalMs = withHeartbeatJitter(getRemoteHeartbeatIntervalMs(PRESENCE_INTERVAL_MS))
    const intervalId = window.setInterval(() => send(), intervalMs)

    const handleOnline = () => send(true)
    const handleOffline = () => reportDevicePresence(location.pathname, false)
    const handleVisible = () => {
      if (!document.hidden) send(true)
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    document.addEventListener('visibilitychange', handleVisible)

    return () => {
      window.clearInterval(intervalId)
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      document.removeEventListener('visibilitychange', handleVisible)
    }
  }, [enabled, location.pathname])
}
